import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Breadcrumbs,
  Button,
  Center,
  Container,
  Divider,
  Grid,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { DataTable } from 'mantine-datatable';
import { useTract, useDeleteTract } from '../../queries/tracts';
import { useTitleRunsForTract } from '../../queries/title-runs';
import { TractFormDrawer } from './TractFormDrawer';
import { TitleRunFormDrawer } from '../title-runs/TitleRunFormDrawer';
import { useAuthStore } from '../../auth/auth-store';
import { useProjectStore } from '../../project/project-store';

export function TractDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const tractQ = useTract(id);
  const runsQ = useTitleRunsForTract(id);
  const deleteM = useDeleteTract();
  const [editOpen, setEditOpen] = useState(false);
  const [newRunOpen, setNewRunOpen] = useState(false);
  const role = useProjectStore((s) => s.projectRole);
  const canWrite = role === 'owner' || role === 'moderator' || role === 'editor';
  useAuthStore();

  if (tractQ.isLoading) {
    return (
      <Center h="60vh">
        <Loader />
      </Center>
    );
  }
  if (tractQ.isError || !tractQ.data) {
    return (
      <Container>
        <Alert color="red" icon={<IconAlertCircle />}>
          Tract not found.
        </Alert>
      </Container>
    );
  }
  const tract = tractQ.data;

  return (
    <Container size="xl">
      <Stack gap="md">
        <Breadcrumbs>
          <Anchor component={Link} to="/tracts">
            Tracts
          </Anchor>
          <Text>{tract.name}</Text>
        </Breadcrumbs>

        <Group justify="space-between" align="flex-end" wrap="nowrap">
          <Stack gap={0}>
            <Title order={2}>{tract.name}</Title>
            <Text c="dimmed" size="sm">
              {tract.state} · {tract.county}
              {tract.gross_acres != null && (
                <>
                  {' · '}
                  {Number(tract.gross_acres).toLocaleString()} acres
                </>
              )}
            </Text>
          </Stack>

          {canWrite && (
            <Group gap="xs">
              <Tooltip label="Edit tract">
                <ActionIcon
                  variant="default"
                  size="lg"
                  onClick={() => setEditOpen(true)}
                  aria-label="edit tract"
                >
                  <IconEdit size={18} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Delete tract">
                <ActionIcon
                  variant="default"
                  color="red"
                  size="lg"
                  onClick={() =>
                    modals.openConfirmModal({
                      title: 'Delete tract?',
                      children: (
                        <Text size="sm">
                          This soft-deletes the tract; title runs remain visible in the DB but
                          hidden from this UI.
                        </Text>
                      ),
                      labels: { confirm: 'Delete', cancel: 'Cancel' },
                      confirmProps: { color: 'red' },
                      onConfirm: async () => {
                        try {
                          await deleteM.mutateAsync(tract.id);
                          notifications.show({
                            color: 'pine',
                            title: 'Tract deleted',
                            message: tract.name,
                          });
                        } catch (err) {
                          notifications.show({
                            color: 'red',
                            title: 'Delete failed',
                            message: (err as Error).message,
                          });
                        }
                      },
                    })
                  }
                  aria-label="delete tract"
                >
                  <IconTrash size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          )}
        </Group>

        <Paper withBorder p="md" radius="md">
          <Grid>
            <InfoCell label="Township" value={tract.township} />
            <InfoCell label="Range" value={tract.range} />
            <InfoCell label="Section" value={tract.section} />
            <InfoCell label="Block" value={tract.block} />
            <InfoCell label="Lot" value={tract.lot} />
            <InfoCell label="Abstract" value={tract.abstract} />
            <InfoCell label="Survey" value={tract.survey} span={6} />
            <InfoCell label="APN" value={tract.apn} span={6} />
            <InfoCell label="Legal description" value={tract.description} span={12} />
            <InfoCell label="Notes" value={tract.notes} span={12} />
          </Grid>
        </Paper>

        <Divider />

        <Group justify="space-between" align="flex-end">
          <Title order={3}>Title Runs</Title>
          {canWrite && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setNewRunOpen(true)}>
              New title run
            </Button>
          )}
        </Group>

        <Paper withBorder radius="md">
          <DataTable
            records={runsQ.data ?? []}
            fetching={runsQ.isLoading}
            minHeight={150}
            noRecordsText={runsQ.isError ? 'Failed to load runs' : 'No title runs yet'}
            highlightOnHover
            onRowClick={({ record }) => navigate(`/title-runs/${record.id}`)}
            columns={[
              {
                accessor: 'title',
                title: 'Title',
                render: (r) => (
                  <Group gap={6} wrap="nowrap">
                    <Link to={`/title-runs/${r.id}`} style={{ textDecoration: 'none' }}>
                      {r.title}
                    </Link>
                    {r.kind === 'supplemental' && (
                      <Badge size="xs" color="grape" variant="light">
                        suppl.
                      </Badge>
                    )}
                    {r.closed_at && (
                      <Badge size="xs" color="gray" variant="filled">
                        closed
                      </Badge>
                    )}
                  </Group>
                ),
              },
              { accessor: 'status', title: 'Status', width: 140 },
              {
                accessor: 'through_date',
                title: 'Through',
                width: 120,
                render: (r) => r.through_date ?? '—',
              },
              {
                accessor: 'updated_at',
                title: 'Updated',
                width: 160,
                render: (r) => dayjs(r.updated_at).format('YYYY-MM-DD HH:mm'),
              },
            ]}
          />
        </Paper>

        <TractFormDrawer opened={editOpen} onClose={() => setEditOpen(false)} tract={tract} />
        <TitleRunFormDrawer
          opened={newRunOpen}
          onClose={() => setNewRunOpen(false)}
          tractId={tract.id}
        />
      </Stack>
    </Container>
  );
}

function InfoCell({
  label,
  value,
  span = 3,
}: {
  label: string;
  value: string | null;
  span?: number;
}) {
  return (
    <Grid.Col span={{ base: 6, sm: span }}>
      <Text size="xs" c="dimmed" fw={500} tt="uppercase">
        {label}
      </Text>
      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
        {value ?? '—'}
      </Text>
    </Grid.Col>
  );
}
